import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import axiosInstance from '../../services/axiosInstance'
import {
  Users, Clock, CheckCircle2, Plane, GraduationCap,
  TrendingUp, TrendingDown, ArrowRight, Sparkles,
  AlertTriangle, FileCheck, Calendar, Earth
} from 'lucide-react'
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts'
import PageHeader from '../../components/common/PageHeader'
import './styles.scss'



export default function Dashboard() {
  const { user } = useAuth()
  const universityName = user?.organizationName || user?.teamName || 'University'

  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        const response = await axiosInstance.get('/api/dashboard/stats')
        setStats(response.data.data)
      } catch (err) {
        console.error("Failed to load dashboard stats", err)
        setError("Could not load dashboard statistics.")
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [])

  if (loading) {
    return (
      <div className="dashboard">
        <PageHeader title="Dashboard" subtitle="Overview of international admissions" />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(37,99,235,0.2)', borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="dashboard">
        <PageHeader title="Dashboard" subtitle="Overview of international admissions" />
        <div style={{ color: '#dc2626', padding: '2rem', background: '#fef2f2', borderRadius: '12px' }}>
          {error || 'Failed to load data.'}
        </div>
      </div>
    )
  }

  // Map backend stats to cards
  const STAT_CARDS = [
    { label: 'Total Applications', value: stats.statCards?.totalApplications || 0, trend: 'up',   icon: Users,          color: '#2563EB', bg: '#EFF6FF' },
    { label: 'Under Review',       value: stats.statCards?.underReview || 0,       trend: 'up',   icon: Clock,          color: '#F59E0B', bg: '#FFFBEB' },
    { label: 'Admitted',           value: stats.statCards?.admitted || 0,          trend: 'up',   icon: CheckCircle2,   color: '#10B981', bg: '#ECFDF5' },
    { label: 'I-20 Ready',         value: stats.statCards?.i20Ready || 0,          trend: 'up',   icon: Plane,          color: '#8B5CF6', bg: '#F5F3FF' },
    { label: 'Enrolled',           value: stats.statCards?.enrolled || 0,          trend: 'up',   icon: GraduationCap,  color: '#06B6D4', bg: '#ECFEFF' },
  ]

  const maxFunnelValue = stats.funnelData && stats.funnelData.length > 0 ? stats.funnelData[0].value : 1;
  const maxCountryValue = stats.countryData && stats.countryData.length > 0 ? stats.countryData[0].value : 1;

  return (
    <div className="dashboard">
      <PageHeader 
        title="Dashboard" 
        subtitle="Overview of international admissions" 
      />

      {/* Stat Cards */}
      <div className="stat-cards">
        {STAT_CARDS.map((card) => (
          <div className="stat-card" key={card.label}>
            <div className="stat-card__icon" style={{ background: card.bg, color: card.color }}>
              <card.icon size={22} />
            </div>
            <div className="stat-card__body">
              <span className="stat-card__label">{card.label}</span>
              <span className="stat-card__value">{card.value.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Middle Row: Funnel · Donut · AI Insights */}
      <div className="dash-grid-3">
        {/* Application Funnel */}
        <div className="dash-card">
          <h3>Application Funnel</h3>
          <div className="funnel-chart">
            {(stats.funnelData || []).map((item) => (
              <div className="funnel-row" key={item.name}>
                <span className="funnel-label">{item.name}</span>
                <div className="funnel-bar-wrap">
                  <div
                    className="funnel-bar"
                    style={{ width: `${Math.max(2, (item.value / Math.max(1, maxFunnelValue)) * 100)}%` }}
                  />
                </div>
                <span className="funnel-value">{item.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Applications by Status (Donut) */}
        <div className="dash-card">
          <h3>Applications by Status</h3>
          <div className="donut-container">
            {stats.statusData && stats.statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={stats.statusData}
                    cx="50%" cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {stats.statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color || '#2563EB'} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">No status data available</div>
            )}
          </div>
          <div className="donut-legend">
            {(stats.statusData || []).map((entry, i) => (
              <div className="legend-item" key={i}>
                <span className="legend-dot" style={{ background: entry.color || '#2563EB' }} />
                <span className="legend-name">{entry.name}</span>
                <span className="legend-val">({entry.value})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Countries of Citizenship */}
        <div className="dash-card">
          <h3><Earth size={18} className="icon-heading" /> Top Countries</h3>
          <div className="country-list">
            {(stats.countryData || []).length > 0 ? (
              (stats.countryData || []).map((c) => (
                <div className="country-row" key={c.name}>
                  <span className="country-name">{c.name}</span>
                  <div className="country-bar-wrap">
                    <div
                      className="country-bar"
                      style={{ width: `${Math.max(2, (c.value / Math.max(1, maxCountryValue)) * 100)}%` }}
                    />
                  </div>
                  <span className="country-val">{c.value}</span>
                  <span className="country-pct">({c.pct})</span>
                </div>
              ))
            ) : (
              <div className="empty-state">No demographic data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row: Activity & Tasks */}
      <div className="dash-grid-2">
        <div className="dash-card">
          <h3>Recent Activity</h3>
          <div className="activity-feed">
            {stats.activities && stats.activities.length > 0 ? (
              stats.activities.map((a, i) => (
                <div className="activity-item" key={i}>
                  <div className="activity-dot" />
                  <div className="activity-body">
                    <p>{a.text}</p>
                    <span className="activity-time">{a.time}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">No recent activity.</div>
            )}
          </div>
        </div>

        <div className="dash-card ai-insights-card">
          <div className="ai-header">
            <h3><Sparkles size={18} className="ai-icon" /> AI Insights</h3>
            <Link to="/ai-insights" className="view-all-link">View All &rarr;</Link>
          </div>
          {stats.aiInsights && stats.aiInsights.length > 0 ? (
            <ul className="ai-list">
              {stats.aiInsights.map((insight, i) => (
                <li key={i}>
                  <Sparkles size={14} className="ai-icon" />
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="empty-state">No AI insights generated yet.</div>
          )}
        </div>
      </div>
    </div>
  )
}
